import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getHauls, deleteHaul, Haul } from '@/services/haulService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, Package, Youtube } from 'lucide-react';

const HaulList = () => {
  const [hauls, setHauls] = useState<Haul[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const fetchHauls = async () => {
    try {
      setLoading(true);
      const data = await getHauls();
      setHauls(data);
    } catch (error) {
      console.error('Error fetching hauls:', error);
      toast.error('Failed to load hauls. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHauls();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      setDeleting(true);
      await deleteHaul(id);
      setHauls((prev) => prev.filter((h) => h.id !== id));
      toast.success('Haul deleted successfully');
    } catch (error) {
      console.error('Error deleting haul:', error);
      toast.error('Failed to delete haul. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hauls</h1>
          <p className="text-muted-foreground">Manage your hauls</p>
        </div>
        <Link to="/admin/hauls/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Haul
          </Button>
        </Link>
      </div>

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <CardTitle>Hauls</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `${hauls.length} haul(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading hauls...</span>
              </div>
            </div>
          ) : hauls.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <Package className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No hauls yet</h3>
                <p className="text-muted-foreground">Create your first haul</p>
              </div>
              <Link to="/admin/hauls/new">
                <Button variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Haul
                </Button>
              </Link>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Thumbnail</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>YouTube</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hauls.map((haul) => (
                    <TableRow key={haul.id}>
                      <TableCell>
                        <div className="w-12 h-12 rounded-md bg-muted overflow-hidden">
                          {haul.thumbnail_image ? (
                            <img
                              src={haul.thumbnail_image}
                              alt={haul.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{haul.name}</p>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {Array.isArray(haul.item_ids) ? haul.item_ids.length : 0} item(s)
                        </span>
                      </TableCell>
                      <TableCell>
                        {(haul as any).youtube_link ? (
                          <a
                            href={(haul as any).youtube_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-primary hover:underline"
                          >
                            <Youtube className="h-4 w-4" />
                            <span className="text-sm">Open</span>
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/admin/hauls/${haul.id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-background border border-border/50">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the haul "{haul.name}". This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDelete(haul.id)}
                                  disabled={deleting}
                                >
                                  {deleting ? 'Deleting...' : 'Delete'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HaulList;
